import React from 'react';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { BroadcastIcon, FullySoldIcon, LightingBoltIcon } from 'src/svgs';
import { WarningSolidIcon } from 'src/svgs/Viagogo';

import * as styles from './RollupMetrics.css';

export const BaseRollupMetrics = ({
  children,
  tooltipContent,
}: {
  children: React.ReactNode;
  tooltipContent?: string;
}) => {
  const { isDarkMode } = useSiteTheme();
  if (!tooltipContent) {
    return (
      <Stack
        className={styles.baseRollupMetrics}
        alignItems="center"
        justifyContent="center"
        gap="s"
      >
        {children}
      </Stack>
    );
  }

  return (
    <Tooltip
      contentVariant={isDarkMode ? 'light' : 'darkGray'}
      triggerContent={
        <Stack
          className={styles.baseRollupMetrics}
          alignItems="center"
          justifyContent="center"
          gap="s"
        >
          {children}
        </Stack>
      }
      align="center"
    >
      {tooltipContent}
    </Tooltip>
  );
};

export const SectionsRollupMetrics = ({ sections }: { sections: string[] }) => {
  const displaySections = sections.join(', ');
  return (
    <BaseRollupMetrics tooltipContent={displaySections}>
      {displaySections}
    </BaseRollupMetrics>
  );
};

export const BroadcastStatusRollupMetrics = ({
  activeBroadcastCount,
  soldCount,
  totalListingCount,
}: {
  activeBroadcastCount: number;
  soldCount: number;
  totalListingCount: number;
}) => {
  if (soldCount > 0) {
    return (
      <Stack
        className={styles.split}
        alignItems="center"
        justifyContent="center"
      >
        <Stack
          className={styles.broadcastRollupMetricsFirst}
          alignItems="center"
          justifyContent="center"
          gap="s"
        >
          <BroadcastIcon size={vars.iconSize.s} />
          {`${activeBroadcastCount} of ${totalListingCount}`}
        </Stack>
        <Stack
          className={styles.broadcastRollupMetricsLast}
          alignItems="center"
          justifyContent="center"
          gap="s"
        >
          <FullySoldIcon
            size={vars.iconSize.s}
            fill={vars.color.iconDisabled}
          />
          {soldCount}
        </Stack>
      </Stack>
    );
  }
  return (
    <BaseRollupMetrics>
      <BroadcastIcon size={vars.iconSize.s} />
      {`${activeBroadcastCount} of ${totalListingCount}`}
    </BaseRollupMetrics>
  );
};

export const AutopriceCountRollupMetrics = ({
  isAutoPricedCount,
}: {
  isAutoPricedCount: number;
}) => {
  return (
    <BaseRollupMetrics>
      <LightingBoltIcon fill={vars.color.iconBrand} size={vars.iconSize.xs} />
      {isAutoPricedCount}
    </BaseRollupMetrics>
  );
};

export const PriceConflictRollupMetrics = ({
  priceConflictCount,
}: {
  priceConflictCount: number;
}) => {
  return (
    <BaseRollupMetrics>
      <WarningSolidIcon fill={vars.color.iconError} size={vars.iconSize.s} />
      {priceConflictCount}
    </BaseRollupMetrics>
  );
};
